import { AlbumHead, useAlbumView, ViewToggle } from "@/components/book/ViewToggle";
import { usePhotoParam } from "@/hooks/usePhotoParam";
import { useCallback, useEffect, useRef, useState } from "react";
import { useParams } from "react-router-dom";
import { api } from "@/lib/api";
import { useFeed } from "@/hooks/useFeed";
import { BookGrid } from "@/components/book/BookGrid";
import { Lightbox } from "@/components/library/Lightbox";
import { Play } from "lucide-react";
import { ThemeBackdrop } from "@/components/theme/ThemeBackdrop";
import { Slideshow } from "@/components/theme/Slideshow";
import { pageClass, themeVars } from "@/lib/themes";
import { YearBook, yearBookSlides } from "@/components/yearbook/YearBook";
import { OfflineBanner } from "@/components/layout/OfflineBanner";
import { APP_NAME } from "@/lib/brand";

export default function PublicAlbumPage() {
  const { token } = useParams();
  const [show, setShow] = useState(false);
  const [album, setAlbum] = useState(null);
  const [error, setError] = useState(false);
  const fetchPage = useCallback(async (cursor) => {
    try {
      const { data } = await api.get(`/public/albums/${token}`, { params: { cursor } });
      setAlbum(data.album);
      return data;
    } catch {
      setError(true);
      return { items: [], next_cursor: null };
    }
  }, [token]);
  const feed = useFeed(fetchPage, { poll: false });
  const [view, setView] = useAlbumView("album", "thumbs");
  const [ybView, setYbView] = useAlbumView("yearbook", "book");
  const photo = usePhotoParam(feed);
  const grid = useRef(null);
  const closePhoto = () => {
    const last = feed.items[photo.index];
    photo.close();
    if (last) requestAnimationFrame(() => grid.current?.scrollToItem(last.id));
  };
  const [yb, setYb] = useState(null);
  useEffect(() => { if (album?.yearbook) api.get(`/public/albums/${token}/yearbook`).then((r) => setYb(r.data)); }, [album?.yearbook, token]);
  const open = photo.open;
  const bookMode = album?.yearbook ? ybView === "book" : false;
  const mode = album?.yearbook ? "thumbs" : view;

  if (error) {
    return (
      <div className="min-h-screen flex items-center justify-center p-6" data-testid="public-album-error">
        <div className="book-page book-cover !w-64"><span className="flex-1" /><span className="book-cover-title">This link is no longer available.</span></div>
      </div>
    );
  }
  return (
    <div className={`public-wrap themed-album ${pageClass(album?.theme)}`} style={themeVars(album?.theme)} data-testid="public-album-page" data-theme={album?.theme?.key}>
      {album && <ThemeBackdrop theme={album.theme} />}
      <OfflineBanner />
      <div className="serif public-brand" data-testid="public-brand">{APP_NAME}</div>
      {album?.yearbook && yb && bookMode && (
        <YearBook title={album.name} data={yb} canEdit={false} head={(t) => <AlbumHead album={album}>{t}</AlbumHead>}
          toolbar={<><button className="tool-btn" onClick={() => setShow(true)} data-testid="public-slideshow-button"><Play size={16} /> <span>Slideshow</span></button><ViewToggle value={ybView} onChange={setYbView} /></>} />
      )}
      {album && !bookMode && (
        <AlbumHead album={album}>
          {feed.items.length > 0 && <button className="tool-btn" onClick={() => setShow(true)} data-testid="public-slideshow-button"><Play size={16} /> <span>Slideshow</span></button>}
          {album.yearbook ? <ViewToggle value={ybView} onChange={setYbView} /> : <ViewToggle value={view} onChange={setView} />}
        </AlbumHead>
      )}
      {album && !bookMode && (
        <BookGrid ref={grid} items={feed.items} timeline={false} hasMore={feed.hasMore} loading={feed.loading} onLoadMore={feed.loadMore} mode={mode}
          selecting={false} onOpen={open} />
      )}
      {show && album.yearbook && yb && <Slideshow items={yearBookSlides(yb)} hasMore={false} theme={album.theme} onClose={() => setShow(false)} />}
      {show && !album.yearbook && <Slideshow items={feed.items} hasMore={feed.hasMore} onLoadMore={feed.loadMore} theme={album.theme} onClose={() => setShow(false)} />}
      {photo.index !== null && (
        <Lightbox items={feed.items} index={photo.index} onIndex={photo.setIndex} onClose={closePhoto} onNearEnd={feed.loadMore} total={album?.item_count} />
      )}
    </div>
  );
}
